/**
 * 全屏流体：GPU 上的 Stable Fluids（平流 / 涡度 / 散度 / 压力投影），做法和参数同原型。
 * 速度场决定最后一道画面处理里的扭曲和色散，染色场叠一层淡淡的颜色。
 * 指针划过、点空白处、入场爆炸都往里面加「推力」（splat）。
 * 十秒没有新的推力时，场早已衰减到看不出来，清零后停算，省电。
 */
import * as THREE from 'three';

const VERT = /* glsl */ `
uniform vec2 texelSize;
varying vec2 vUv, vL, vR, vT, vB;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  vL = vUv - vec2(texelSize.x, 0.0);
  vR = vUv + vec2(texelSize.x, 0.0);
  vT = vUv + vec2(0.0, texelSize.y);
  vB = vUv - vec2(0.0, texelSize.y);
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const HEAD = 'uniform vec2 texelSize;\nvarying vec2 vUv, vL, vR, vT, vB;\n';

const SHADERS = {
  splat: `uniform sampler2D uTarget; uniform float aspectRatio, radius; uniform vec3 color; uniform vec2 point;
    void main() { vec2 p = vUv - point; p.x *= aspectRatio; gl_FragColor = vec4(texture2D(uTarget, vUv).xyz + exp(-dot(p, p) / radius) * color, 1.0); }`,
  advect: `uniform sampler2D uVelocity, uSource; uniform float dt, dissipation;
    void main() { vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize; gl_FragColor = texture2D(uSource, coord) / (1.0 + dissipation * dt); }`,
  divergence: `uniform sampler2D uVelocity;
    void main() {
      float L = texture2D(uVelocity, vL).x, R = texture2D(uVelocity, vR).x, T = texture2D(uVelocity, vT).y, B = texture2D(uVelocity, vB).y;
      vec2 C = texture2D(uVelocity, vUv).xy;
      if (vL.x < 0.0) L = -C.x;
      if (vR.x > 1.0) R = -C.x;
      if (vT.y > 1.0) T = -C.y;
      if (vB.y < 0.0) B = -C.y;
      gl_FragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);
    }`,
  curl: `uniform sampler2D uVelocity;
    void main() {
      float L = texture2D(uVelocity, vL).y, R = texture2D(uVelocity, vR).y, T = texture2D(uVelocity, vT).x, B = texture2D(uVelocity, vB).x;
      gl_FragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);
    }`,
  vorticity: `uniform sampler2D uVelocity, uCurl; uniform float curl, dt;
    void main() {
      float L = texture2D(uCurl, vL).x, R = texture2D(uCurl, vR).x, T = texture2D(uCurl, vT).x, B = texture2D(uCurl, vB).x, C = texture2D(uCurl, vUv).x;
      vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
      force /= length(force) + 0.0001;
      force *= curl * C;
      force.y *= -1.0;
      vec2 v = texture2D(uVelocity, vUv).xy + force * dt;
      gl_FragColor = vec4(clamp(v, -1000.0, 1000.0), 0.0, 1.0);
    }`,
  pressure: `uniform sampler2D uPressure, uDivergence;
    void main() {
      float L = texture2D(uPressure, vL).x, R = texture2D(uPressure, vR).x, T = texture2D(uPressure, vT).x, B = texture2D(uPressure, vB).x;
      gl_FragColor = vec4((L + R + B + T - texture2D(uDivergence, vUv).x) * 0.25, 0.0, 0.0, 1.0);
    }`,
  gradient: `uniform sampler2D uPressure, uVelocity;
    void main() {
      float L = texture2D(uPressure, vL).x, R = texture2D(uPressure, vR).x, T = texture2D(uPressure, vT).x, B = texture2D(uPressure, vB).x;
      gl_FragColor = vec4(texture2D(uVelocity, vUv).xy - vec2(R - L, T - B), 0.0, 1.0);
    }`,
  fade: `uniform sampler2D uTexture; uniform float uValue; void main() { gl_FragColor = uValue * texture2D(uTexture, vUv); }`,
} as const;

type Pass = keyof typeof SHADERS;

interface Splat {
  x: number;
  y: number;
  dx: number;
  dy: number;
  color: { r: number; g: number; b: number } | null;
  radius: number;
}

class DoubleTarget {
  read: THREE.WebGLRenderTarget;
  write: THREE.WebGLRenderTarget;
  constructor(make: () => THREE.WebGLRenderTarget) {
    this.read = make();
    this.write = make();
  }
  swap(): void {
    const t = this.read;
    this.read = this.write;
    this.write = t;
  }
  dispose(): void {
    this.read.dispose();
    this.write.dispose();
  }
}

export class Fluid {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.Camera();
  private readonly quad: THREE.Mesh;
  private readonly passes: Record<Pass, THREE.ShaderMaterial>;
  private readonly texel = new THREE.Vector2();
  private readonly queue: Splat[] = [];
  private velocity!: DoubleTarget;
  private dye!: DoubleTarget;
  private pressure!: DoubleTarget;
  private divergence!: THREE.WebGLRenderTarget;
  private curl!: THREE.WebGLRenderTarget;
  private aspect = 1;
  private simSize = '';
  /** 距上一次推力的秒数；清零后不再计算，直到下一次推力 */
  private quiet = Number.POSITIVE_INFINITY;
  private settled = true;
  private readonly renderer: THREE.WebGLRenderer;
  private simRes: number;
  private dyeRes: number;
  /** 多久没有新推力就停算（秒）；画质降档时缩短（quality.ts） */
  idleAfter = 10;
  /** 关掉时不再计算、不接受推力，最后一道画面处理也不再扭曲 */
  enabled = true;
  private width = 1;
  private height = 1;

  constructor(renderer: THREE.WebGLRenderer, opts: { simRes: number; dyeRes: number }) {
    this.renderer = renderer;
    this.simRes = opts.simRes;
    this.dyeRes = opts.dyeRes;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    this.quad = new THREE.Mesh(g);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
    const make = (frag: string, uniforms: Record<string, THREE.IUniform>) =>
      new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: HEAD + frag,
        uniforms: { texelSize: { value: this.texel }, ...uniforms },
        depthTest: false,
        depthWrite: false,
        blending: THREE.NoBlending,
      });
    const T = () => ({ value: null as THREE.Texture | null }), N = (v = 0) => ({ value: v });
    this.passes = {
      splat: make(SHADERS.splat, { uTarget: T(), aspectRatio: N(1), radius: N(0.002), color: { value: new THREE.Vector3() }, point: { value: new THREE.Vector2() } }),
      advect: make(SHADERS.advect, { uVelocity: T(), uSource: T(), dt: N(), dissipation: N(1) }),
      divergence: make(SHADERS.divergence, { uVelocity: T() }),
      curl: make(SHADERS.curl, { uVelocity: T() }),
      vorticity: make(SHADERS.vorticity, { uVelocity: T(), uCurl: T(), curl: N(18), dt: N() }),
      pressure: make(SHADERS.pressure, { uPressure: T(), uDivergence: T() }),
      gradient: make(SHADERS.gradient, { uPressure: T(), uVelocity: T() }),
      fade: make(SHADERS.fade, { uTexture: T(), uValue: N(0.8) }),
    };
    this.resize(1, 1);
  }

  /** 速度场：最后一道画面处理拿它扭曲画面 */
  get velocityTexture(): THREE.Texture {
    return this.velocity.read.texture;
  }

  get dyeTexture(): THREE.Texture {
    return this.dye.read.texture;
  }

  /** 画面的 CSS 尺寸变了：长宽比变了才重建各个场（流体会清零） */
  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.aspect = width / Math.max(1, height);
    const res = (base: number) => {
      const big = Math.round(base * Math.max(this.aspect, 1 / this.aspect));
      return this.aspect >= 1 ? { w: big, h: base } : { w: base, h: big };
    };
    const s = res(this.simRes), d = res(this.dyeRes), key = `${s.w}x${s.h}/${d.w}x${d.h}`;
    if (key === this.simSize) return;
    this.simSize = key;
    const target = (w: number, h: number, filter: THREE.MagnificationTextureFilter) =>
      new THREE.WebGLRenderTarget(w, h, {
        type: THREE.HalfFloatType,
        format: THREE.RGBAFormat,
        minFilter: filter,
        magFilter: filter,
        wrapS: THREE.ClampToEdgeWrapping,
        wrapT: THREE.ClampToEdgeWrapping,
        depthBuffer: false,
        stencilBuffer: false,
        generateMipmaps: false,
      });
    for (const t of [this.velocity, this.dye, this.pressure]) t?.dispose();
    this.divergence?.dispose();
    this.curl?.dispose();
    this.velocity = new DoubleTarget(() => target(s.w, s.h, THREE.LinearFilter));
    this.dye = new DoubleTarget(() => target(d.w, d.h, THREE.LinearFilter));
    this.pressure = new DoubleTarget(() => target(s.w, s.h, THREE.NearestFilter));
    this.divergence = target(s.w, s.h, THREE.NearestFilter);
    this.curl = target(s.w, s.h, THREE.NearestFilter);
    this.texel.set(1 / s.w, 1 / s.h);
    this.clearAll();
  }

  private clearAll(): void {
    const r = this.renderer, prevColor = r.getClearColor(new THREE.Color()), prevAlpha = r.getClearAlpha();
    r.setClearColor(0x000000, 0);
    for (const t of [this.velocity.read, this.velocity.write, this.dye.read, this.dye.write, this.pressure.read, this.pressure.write, this.divergence, this.curl]) {
      r.setRenderTarget(t);
      r.clear();
    }
    r.setRenderTarget(null);
    r.setClearColor(prevColor, prevAlpha);
    this.settled = true;
  }

  /** 换分辨率（画质降档、升档时）；场会清零 */
  setResolution(simRes: number, dyeRes: number): void {
    if (simRes === this.simRes && dyeRes === this.dyeRes) return;
    this.simRes = simRes;
    this.dyeRes = dyeRes;
    this.resize(this.width, this.height);
  }

  /** 加一股推力：x、y 是 0–1 的画面坐标（原点在左下），dx、dy 是速度 */
  splat(x: number, y: number, dx: number, dy: number, color: Splat['color'] = null, radius = 0.0022): void {
    if (this.enabled && this.queue.length < 32) this.queue.push({ x, y, dx, dy, color, radius });
  }

  /** 点空白处：一圈向外的小涟漪 */
  burst(x: number, y: number, strength = 1, color: Splat['color'] = null): void {
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      this.splat(x + (Math.cos(a) * 0.014) / this.aspect, y + Math.sin(a) * 0.014, Math.cos(a) * 380 * strength, Math.sin(a) * 380 * strength, color, 0.0009);
    }
  }

  /** 入场爆炸：两圈向外的强推力，把整个画面「震开」 */
  shockwave(x: number, y: number, k = 1): void {
    for (const [rad, sp] of [
      [0.05, 1],
      [0.12, 0.65],
    ] as const) {
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        this.splat(x + (Math.cos(a) * rad) / this.aspect, y + Math.sin(a) * rad, Math.cos(a) * 950 * sp * k, Math.sin(a) * 950 * sp * k, { r: 0.05 * sp, g: 0.03 * sp, b: 0.045 * sp }, 0.0028);
      }
    }
  }

  private blit(pass: Pass, target: THREE.WebGLRenderTarget): void {
    this.quad.material = this.passes[pass];
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.scene, this.camera);
  }

  private applySplat(s: Splat): void {
    const u = this.passes.splat.uniforms;
    u.aspectRatio!.value = this.aspect;
    (u.point!.value as THREE.Vector2).set(s.x, s.y);
    u.radius!.value = this.aspect > 1 ? s.radius * this.aspect : s.radius;
    u.uTarget!.value = this.velocity.read.texture;
    (u.color!.value as THREE.Vector3).set(s.dx, s.dy, 0);
    this.blit('splat', this.velocity.write);
    this.velocity.swap();
    if (s.color) {
      u.uTarget!.value = this.dye.read.texture;
      (u.color!.value as THREE.Vector3).set(s.color.r, s.color.g, s.color.b);
      this.blit('splat', this.dye.write);
      this.dye.swap();
    }
  }

  step(dt: number): void {
    if (!this.enabled) this.queue.length = 0;
    this.quiet = this.queue.length ? 0 : this.quiet + dt;
    if (!this.enabled || this.quiet > this.idleAfter) {
      if (!this.settled) this.clearAll();
      return;
    }
    this.settled = false;
    dt = Math.min(dt, 1 / 30);
    const r = this.renderer, autoClear = r.autoClear;
    // 每一遍都整屏覆盖，不用先清
    r.autoClear = false;
    for (const s of this.queue.splice(0, 32)) this.applySplat(s);
    const P = this.passes;
    P.curl.uniforms.uVelocity!.value = this.velocity.read.texture;
    this.blit('curl', this.curl);
    P.vorticity.uniforms.uVelocity!.value = this.velocity.read.texture;
    P.vorticity.uniforms.uCurl!.value = this.curl.texture;
    P.vorticity.uniforms.dt!.value = dt;
    this.blit('vorticity', this.velocity.write);
    this.velocity.swap();
    P.divergence.uniforms.uVelocity!.value = this.velocity.read.texture;
    this.blit('divergence', this.divergence);
    P.fade.uniforms.uTexture!.value = this.pressure.read.texture;
    this.blit('fade', this.pressure.write);
    this.pressure.swap();
    P.pressure.uniforms.uDivergence!.value = this.divergence.texture;
    for (let i = 0; i < 14; i++) {
      P.pressure.uniforms.uPressure!.value = this.pressure.read.texture;
      this.blit('pressure', this.pressure.write);
      this.pressure.swap();
    }
    P.gradient.uniforms.uPressure!.value = this.pressure.read.texture;
    P.gradient.uniforms.uVelocity!.value = this.velocity.read.texture;
    this.blit('gradient', this.velocity.write);
    this.velocity.swap();
    const a = P.advect.uniforms;
    a.dt!.value = dt;
    a.uVelocity!.value = this.velocity.read.texture;
    a.uSource!.value = this.velocity.read.texture;
    a.dissipation!.value = 1.4;
    this.blit('advect', this.velocity.write);
    this.velocity.swap();
    a.uVelocity!.value = this.velocity.read.texture;
    a.uSource!.value = this.dye.read.texture;
    a.dissipation!.value = 2.2;
    this.blit('advect', this.dye.write);
    this.dye.swap();
    r.setRenderTarget(null);
    r.autoClear = autoClear;
  }
}
